<template>
  <div
    class="multiselect-root"
    @click="handleClick"
    v-click-outside="handleFocusOut"
  >
    <div>
      <input
        v-show="showInput || tags.length === 0"
        class="multiselect"
        type="text"
        ref="multiselectInput"
        v-model="searchText"
        style="width: 100%"
        @input="$emit('search-change', searchText)"
      />
    </div>

    <div class="multiselect-tag-wrapper" v-if="!showInput && tags.length > 0">
      <span
        class="multiselect-tag"
        v-for="(tag, index) in tags"
        v-bind:key="index"
      >
        {{ tag[trackBy] }}
        <span
          class="multiselect-tag__remove-icon"
          @click.stop="removeSelected(tag[valueProp])"
          >&times;</span
        >
      </span>
    </div>

    <div class="dropdown-wrapper" v-if="showDropdown">
      <ul class="dropdown-list">
        <li
          v-for="option in options"
          :value="option[valueProp]"
          v-bind:key="option.id"
          @click.stop="handleSelect(option[valueProp])"
          class="dropdown-item"
          :class="option.isActive ? 'dropdown-item--active' : ''"
        >
          {{ option[trackBy] }}
        </li>
        <div
          v-if="options.length > 0 && isMoreOptions"
          v-observe-visibility="reachedEndOfList"
        />
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, computed, watchEffect } from "vue";

export default {
  name: "Multiselect",
  emits: [
    "open",
    "close",
    "select",
    "deselect",
    "input",
    "search-change",
    "tag",
    "update:modelValue",
    "change",
    "clear",
    "reached-end",
  ],
  props: {
    modelValue: {
      type: Array,
      required: false,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    optionsInit: {
      type: Array,
      required: false,
      default: () => [],
    },
    isMoreOptions: {
      type: Boolean,
      required: false,
    },
    trackBy: {
      type: String,
      required: false,
      default: "label",
    },
    valueProp: {
      type: String,
      required: false,
      default: "value",
    },
  },
  setup(props, context) {
    const tags = computed(() => {
      const data = [];
      selectedOptionsIds.value.forEach((selectedOptionId) => {
        let flag = true;
        if (props.optionsInit) {
          props.optionsInit.forEach((option) => {
            if (selectedOptionId === option[props.valueProp]) {
              data.push(option);
              flag = false;
            }
          });
        }

        if (flag) {
          props.options.forEach((option) => {
            if (selectedOptionId === option[props.valueProp]) {
              data.push(option);
            }
          });
        }
      });
      return data;
    });
    const selectedOptionsIds = ref(props.modelValue);
    const showDropdown = ref(false);
    const showInput = ref(false);
    const searchText = ref("");

    const multiselectInput = ref(null);

    watchEffect(() => {
      props.options.forEach((option) => {
        option.isActive = false;
      });

      selectedOptionsIds.value.forEach((selectedOptionId) => {
        props.options.forEach((option) => {
          if (selectedOptionId === option[props.valueProp]) {
            option.isActive = true;
          }
        });
      });
    });

    function handleSelect(optionId) {
      showInput.value = false;
      showDropdown.value = false;

      const existingOption = selectedOptionsIds.value.find(
        (item) => item === optionId
      );
      if (existingOption) {
        return;
      }

      selectedOptionsIds.value.push(optionId);

      context.emit("update:modelValue", selectedOptionsIds.value);
    }

    function removeSelected(optionId) {
      selectedOptionsIds.value.forEach((selectedOptionId, index) => {
        if (selectedOptionId === optionId) {
          selectedOptionsIds.value.splice(index, 1);
        }
      });

      showDropdown.value = false;

      context.emit("update:modelValue", selectedOptionsIds.value);
    }

    function handleFocusOut() {
      setTimeout(() => {
        showDropdown.value = false;
        showInput.value = false;
      }, 1);
    }

    function handleClick() {
      setTimeout(() => {
        showDropdown.value = true;
        showInput.value = true;
        searchText.value = "";
        multiselectInput.value.focus();
      }, 0);
    }

    function reachedEndOfList(isVisible) {
      if (isVisible) {
        context.emit("reached-end", searchText.value);
      }
    }

    return {
      handleSelect,
      tags,
      showDropdown,
      removeSelected,
      handleFocusOut,
      showInput,
      multiselectInput,
      handleClick,
      searchText,
      reachedEndOfList,
    };
  },
};
</script>

<style scoped>
.multiselect {
  position: relative;
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  font-size: 1rem;
  height: 32px;
  min-height: 32px;
}

.multiselect-root {
  position: relative;
  width: 100%;
  min-width: 0;
  cursor: pointer;
}

.multiselect-tag-wrapper {
  position: relative;
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  z-index: 9;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
}
.multiselect-tag {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  background-color: #10b981;
  color: #fff;
  padding: 2px 0 2px 6px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 5px;
  overflow-wrap: anywhere;
}
.multiselect-tag__remove-icon {
  display: inline-flex;
  min-width: 28px;
  min-height: 28px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 18px;
  transition: 0.2s ease-in-out;
}
.multiselect-tag__remove-icon:hover {
  color: rgba(255, 0, 0, 0.85);
}

.dropdown-wrapper {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  top: calc(100% + 4px);
  left: 0;
  max-height: min(320px, 45vh);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  position: absolute;
  z-index: 40;
  width: 100%;
}
.dropdown-item {
  display: flex;
  min-height: 40px;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  overflow-wrap: anywhere;
}
.dropdown-item:hover {
  background-color: #e7e7e7;
}
.dropdown-item--active {
  background-color: #e7e7e7;
}

@media (max-width: 640px) {
  .multiselect,
  .multiselect-tag-wrapper {
    min-height: 40px;
    font-size: 16px;
  }

  .dropdown-wrapper {
    max-height: min(360px, 50dvh);
  }

  .dropdown-item {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
